<template>
    <div
        v-if="messageText"
        class="helper-message flex items-center font-normal"
        :class="[
            `helper-text-${state}`,
            size === 'sm' ? 'text-content-sm' : 'text-content-md',
            actionableHelperText ? 'helper-message-with-action' : '',
        ]"
        :role="state !== 'base' ? 'alert' : undefined"
    >
        <span
            v-if="STATE_ICONS[state]"
            class="helper-text-icon inline-flex"
        >
            <DsIcon
                :icon="STATE_ICONS[state]"
                size="sm"
            />
        </span>
        <div>
            <button
                v-if="actionableHelperText"
                class="text-left"
                type="button"
                @click.stop="emit('clickHelperText')"
            >
                {{ messageText }}
            </button>
            <template v-else>{{ messageText }}</template>
        </div>
    </div>
</template>

<script setup lang="ts">
import type {
    HelperMessageEmits,
    HelperMessageInputProps,
    HelperMessageState,
} from '@/components/design-system/utils/helper-message.types';
import type { DsIconProps } from '@ds/DsIcon.vue';
import DsIcon from '@ds/DsIcon.vue';
import { computed } from 'vue';

const STATE_ICONS = {
    base: undefined,
    warning: 'fa-solid fa-circle-exclamation',
    error: 'fa-solid fa-triangle-exclamation',
} as const satisfies Record<HelperMessageState, DsIconProps['icon'] | undefined>;

const props = withDefaults(defineProps<HelperMessageInputProps>(), {
    error: false,
    warning: false,
    helperText: '',
    size: 'md',
    actionableHelperText: false,
});

const emit = defineEmits<HelperMessageEmits>();

const state = computed<HelperMessageState>(() => (props.error ? 'error' : props.warning ? 'warning' : 'base'));

const messageText = computed(() => {
    const message = props.error || props.warning || props.helperText;

    return typeof message === 'string' ? message : '';
});
</script>

<style scoped lang="scss">
.helper-message {
    &:not(.helper-message-with-action) {
        @apply gap-1.5;
    }

    button {
        @apply rounded-md px-1.5 underline;

        &:active {
            @apply no-underline;
        }

        &:focus-visible {
            @apply shadow-outline-field;
            outline: 2px solid theme('colors.ds.gray.800');
        }
    }

    &.helper-text-base {
        @apply text-content-secondary;
    }

    &.helper-text-warning {
        @apply text-content-primary;

        .helper-text-icon {
            @apply text-ds-yellow-400;
        }
    }

    &.helper-text-base,
    &.helper-text-warning {
        button {
            @apply text-content-secondary;

            &:hover,
            &:active {
                @apply text-content-primary;
            }
        }
    }

    &.helper-text-error {
        @apply text-content-error;

        button {
            &:hover {
                @apply text-semantic-destructive-primary-hover;
            }

            &:active {
                @apply text-semantic-destructive-primary-pressed;
            }

            &:focus-visible {
                outline: 2px solid theme('colors.ds.red.600');
            }
        }
    }
}
</style>
